import { call, put, select, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import factory from "utils/getApiHandlerEffect";
import { selectActivationCode, selectUserCidpId } from "scenes/selectors";
import { mfaFlow } from "utils/enum";
import { SagaIterator } from "redux-saga";
import {
  PassCodeRequestPayload,
  QRCodeRequestPayload,
  QRCodeResponse,
  StatusType,
} from "./types";
import {
  afterAppAuthenticatorFetch,
  afterPassCodeSubmission,
  afterStatusFetch,
  loadQRCode,
  loadStatus,
  setError,
  setStatus,
  submitPassCode,
} from "./redux";
import { AuthenticatorStatusEnum, PassCodeSubmissionStatusEnum } from "./enums";
import { currentFlow } from "./selectors";

export function* onLoad(
  action: PayloadAction<QRCodeRequestPayload>
): SagaIterator<void> {
  const { authenticatorType, currentLayout } = action.payload;
  const activationCode = yield select(selectActivationCode);
  const cidpId = yield select(selectUserCidpId);
  const apiHandler = yield call(() => factory());
  try {
    const QRCode: QRCodeResponse = yield call(() =>
      currentLayout === mfaFlow.MFA
        ? apiHandler.putUpdateMFA("app", cidpId, { factorType: "TOTP" })
        : apiHandler.putMFAEnroll(authenticatorType, activationCode)
    );
    yield put(afterAppAuthenticatorFetch(QRCode));
  } catch (e: any) {
    if (e.statusCode === 400) {
      yield put(afterAppAuthenticatorFetch({ qrCodeLink: "", secretCode: "" }));
    } else {
      yield put(setError(true));
    }
  }
}

export function* onPassCodeSubmission(
  action: PayloadAction<PassCodeRequestPayload>
): SagaIterator<void> {
  yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.PROCESSING));
  const activationCode = yield select(selectActivationCode);
  const cidpId = yield select(selectUserCidpId);
  const currentFlowSetup = yield select(currentFlow);
  const { authenticatorType, passcode } = action.payload;
  const apiHandler = yield call(() => factory());
  try {
    yield call(() =>
      currentFlowSetup === mfaFlow.MFA
        ? apiHandler.updatePassCode("app", cidpId, passcode)
        : apiHandler.submitAuthenticatorPassCode(
            authenticatorType,
            activationCode,
            passcode
          )
    );
    yield put(setError(true));
    yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.DONE));
  } catch (e: any) {
    yield put(setError(true));
    yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.ERROR));
  }
}

export function* onLoadStatus(
  action: PayloadAction<QRCodeRequestPayload>
): SagaIterator<void> {
  const { authenticatorType } = action.payload;
  const cidpId = yield select(selectUserCidpId);
  const currentFlowSetup = yield select(currentFlow);
  const activationCode = yield select(selectActivationCode);

  yield put(setStatus(AuthenticatorStatusEnum.PROCESSING));
  const apiHandler = yield call(() => factory());
  try {
    const response: { status: StatusType } = yield call(() =>
      currentFlowSetup === mfaFlow.MFA
        ? apiHandler.getUpdateMfaStatus("app", cidpId)
        : apiHandler.getAuthenticatorStatus(authenticatorType, activationCode)
    );
    yield put(afterStatusFetch(response.status));
  } catch (e: any) {
    if (e.statusCode === 400) {
      yield put(afterStatusFetch(AuthenticatorStatusEnum.ERROR));
    } else {
      yield put(setError(true));
    }
  }
}

// watcher function
export default function* appAuthenticatorsSaga() {
  yield takeLatest(loadQRCode.type, onLoad);
  yield takeLatest(submitPassCode.type, onPassCodeSubmission);
  yield takeLatest(loadStatus.type, onLoadStatus);
}
